import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import MimeType from '@/Enums/MimeType.enum';
import { IAdvertiser } from '@/Interfaces/Advertiser.interface';
import { IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import { ICreative, ICreativeFilters, ICreativeFormData } from '@/Interfaces/Creatives/Creative.interface';
import CreativeTypeService from '@/Services/Creative/CreativeType.service';
import { CREATIVES_DEFAULT_SORT, validFormats } from '@/constants/creativeLibrary';
import { normalizeUrl } from '@/helpers/url';
import { CreativeCreatePayload, CreativeUpdatePayload } from '@/types/creativeLibrary';
import { getAdvertiserControlOption } from './advertisers';
import { formatFileSize } from './byteValueConverter.helpers';
import { getControlOptionsValues } from './filters/filterMapping';
import { getSortFilter } from './grid/getSortFilter';

// Checks whether the bytes at `offset` match an MPEG audio frame sync
const isMpegFrameSync = (bytes: Uint8Array, offset: number): boolean =>
  bytes[offset] === 0xff && (bytes[offset + 1] & 0xe0) === 0xe0;

// Verifies that a file is a structurally valid MP3:
const hasValidMp3Structure = (buffer: ArrayBuffer): boolean => {
  const bytes = new Uint8Array(buffer);
  if (bytes.length < 4) return false;

  let audioOffset = 0;

  // Skip a leading ID3v2 tag if present ("ID3"), using its syncsafe size field.
  if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
    const tagSize =
      ((bytes[6] & 0x7f) << 21) | ((bytes[7] & 0x7f) << 14) | ((bytes[8] & 0x7f) << 7) | (bytes[9] & 0x7f);
    audioOffset = 10 + tagSize;
    // ID3v2.4 may append a 10-byte footer (flags byte, bit 4).
    if (bytes[4] === 0x04 && bytes[5] & 0x10) audioOffset += 10;
  }

  // The audio stream must begin with a frame sync. A misplaced ID3v1 "TAG"
  // block (or any other padding) here means the file was exported incorrectly.
  return isMpegFrameSync(bytes, audioOffset);
};

export const validateFile = (
  file: File,
  format: CreativeType.VIDEO | CreativeType.DISPLAY | CreativeType.AUDIO | CreativeType.HTML5,
  handleError: (error: string) => void,
) => {
  return new Promise(resolve => {
    const reader = new FileReader();

    reader.onload = event => {
      if (!format) {
        handleError('Select format');
        return;
      }

      if (!validFormats[format].acceptedTypes.includes(file.type as MimeType)) {
        handleError(
          `Invalid file extension. Supported extensions: ${validFormats[format].acceptedExtensions.join(', ')}`,
        );
        return;
      }

      if (file.size > validFormats[format].maxFileSize) {
        handleError(`File size should not exceed ${formatFileSize(validFormats[format].maxFileSize)}.`);
        return;
      }

      if (format === CreativeType.VIDEO) {
        const video = document.createElement('video');
        const isMovFile = file.type === 'video/quicktime';

        video.addEventListener('loadedmetadata', () => {
          const width = video.videoWidth;
          const height = video.videoHeight;
          const duration = Math.floor(video.duration);

          if (!width || !height || isNaN(duration)) {
            handleError('Unable to read video metadata. Please ensure the video file is not corrupted.');
            return;
          }

          if (duration > validFormats[format].maxDuration) {
            handleError(`Duration of the video should not exceed ${validFormats[format].maxDuration} seconds.`);
            return;
          }

          resolve(void 0);
        });

        video.addEventListener('error', (error: Event) => {
          const videoElement = error.target as HTMLVideoElement;
          const errorCode = videoElement.error?.code;
          // mov files metadata extraction fails in browsers
          if (errorCode === 4 && isMovFile) {
            resolve(void 0);
            return;
          }

          handleError('Error loading video file. The video is not supported.');
          return;
        });

        video.src = event.target!.result as string;
      } else if (format === CreativeType.DISPLAY) {
        const img = new Image();
        img.onload = () => {
          const { maxWidth, maxHeight } = validFormats[format];
          if (img.width > maxWidth || img.height > maxHeight) {
            handleError(
              `Image dimensions ${img.width}x${img.height}px exceed the maximum allowed size of ${maxWidth}x${maxHeight}px.`,
            );
            return;
          }
          resolve(void 0);
        };
        img.onerror = () => {
          handleError('Unable to read image metadata. Please ensure the image file is not corrupted.');
        };
        img.src = event.target!.result as string;
      } else if (format === CreativeType.AUDIO) {
        file
          .arrayBuffer()
          .then(buffer => {
            if (!hasValidMp3Structure(buffer)) {
              handleError(
                'This file isn’t a valid MP3. It may have been exported incorrectly. Please re-export or convert it to MP3 and try again.',
              );
              return;
            }

            const audio = new Audio();
            audio.addEventListener('loadedmetadata', () => {
              const duration = audio.duration;

              if (duration > validFormats[format].maxDuration) {
                handleError(`Duration of the audio should not exceed ${validFormats[format].maxDuration} seconds.`);
                return;
              }

              resolve(void 0);
            });

            audio.addEventListener('error', () => {
              handleError('Unable to read audio metadata. Please ensure the audio file is not corrupted.');
            });

            audio.src = event.target!.result as string;
          })
          .catch(() => {
            handleError('Unable to read the audio file. Please ensure it is not corrupted.');
          });
      } else if (format === CreativeType.HTML5) {
        resolve(void 0);
      }
    };

    reader.readAsDataURL(file);
  });
};

export const creativeFormDataToApiStructure = (
  formData: ICreativeFormData,
  id?: number,
): CreativeCreatePayload | CreativeUpdatePayload => {
  const payload: CreativeCreatePayload | CreativeUpdatePayload = {
    name: formData.name,
    advertiserId: formData.advertiser!.value,
    type: formData.format,
    alternativeId: formData.alternativeId,
  };

  if (id) {
    (payload as CreativeUpdatePayload).id = id;
  }

  if (CreativeTypeService.isInline(formData.format)) {
    if (!id || (id && formData.file instanceof File)) {
      (payload as CreativeCreatePayload).file = formData.file as File;
    }
    if (formData.clickUrl && CreativeTypeService.hasClickUrl(formData.format)) {
      (payload as CreativeCreatePayload).clickUrl = normalizeUrl(formData.clickUrl);
    }
  } else {
    (payload as CreativeCreatePayload).tagContent = formData.tagContent!;

    if (CreativeTypeService.isWrapper(formData.format) && formData.duration) {
      payload.duration = Number(formData.duration);
    }
  }

  if (formData.pixels) {
    payload.pixels = formData.pixels;
  }

  if (formData.creativeSize) {
    payload.creativeSizeId = formData.creativeSize.value;
  }

  if ([CreativeType.AUDIO].includes(formData.format)) {
    if (formData.companionAssetFileUrl) {
      payload.companionAssetFileUrl = formData.companionAssetFileUrl;
    }

    if (formData.companionAssetImage instanceof File) {
      payload.companionAssetImage = formData.companionAssetImage;
    }

    if (formData.companionAssetThumbnail) {
      payload.companionAssetThumbnail = formData.companionAssetThumbnail;
    }
  }
  return payload;
};

export const creativeToFormData = (
  creative: ICreative,
  creativeSizes: IGroupedControlOption[] | undefined,
): ICreativeFormData => ({
  name: creative.name,
  alternativeId: creative.alternativeId,
  advertiser: getAdvertiserControlOption(creative.advertiser),
  format: creative.type,
  clickUrl: creative.clickUrl,
  file: creative.file,
  tagContent: creative.tagContent,
  duration: creative.duration != null ? String(creative.duration) : '',
  pixels: creative.pixels ?? [],
  creativeSize: creative.creativeSizeId
    ? (creativeSizes?.find(option => option.value === creative.creativeSizeId) ?? null)
    : null,
  companionAssetImage: null,
  companionAssetFileUrl: creative.companionAssetFileUrl,
  companionAssetThumbnail: creative.companionAssetThumbnail,
});

export const mapAdvertisersForCreativeLibrary = ({ id, beeswaxAdvertiser, name }: IAdvertiser) => {
  return {
    value: id,
    label: name,
    beeswaxId: beeswaxAdvertiser?.beeswax_id,
  };
};

export const filterAdvertisersForCreativeLibrary = ({ beeswaxAdvertiser, is_archived }: IAdvertiser) => {
  // Only Beeswax advertisers are now allowed on Creative Library; archived ones are rejected by the API
  const beeswaxId = beeswaxAdvertiser?.beeswax_id;
  return !is_archived && !!beeswaxId && Number.isFinite(beeswaxId) && beeswaxId >= 0;
};

export const mapApiCreativesToTableData = (creatives: ICreative[]) => {
  return creatives.map(creative => ({
    ...creative,
    advertiserName: creative.advertiser?.name,
    clientName: creative.advertiser?.client?.name ?? '',
  }));
};

export const transformCreativeFilters = ({
  sort,
  whitelabels,
  clients,
  advertisers,
  isArchived,
  ...restFilters
}: Omit<ICreativeFilters, 'platform'>) => {
  return {
    ...restFilters,
    ...(isArchived && { isArchived }),
    sort: sort?.length ? getSortFilter(sort) : CREATIVES_DEFAULT_SORT,
    whitelabelIds: getControlOptionsValues(whitelabels),
    clientIds: getControlOptionsValues(clients),
    advertiserIds: getControlOptionsValues(advertisers),
  };
};
